import { ListPage } from "@services/common/types";
import API from "@services/root/rootApi";
import type {
  Role,
  User,
  UserFilters,
  AuthenticatedUser,
  UserToAdd,
  UserSelfActivation,
  EditableRole,
} from "./types";
import { Dispatch, SetStateAction } from "react";
export const getRoles = async (accountId?: string[]): Promise<Role[]> => {
  const params = accountId && { accountId };
  return (await API.post<Role[]>("/symphony-api/provisioning/role", params))
    .data;
};

export const getRole = async (id: Role["id"]): Promise<Role> =>
  (await API.get<Role>(`/symphony-api/provisioning/role/${id}`)).data;

export const deleteRoles = async (ids: Role["id"][]) =>
  await API.delete("/symphony-api/provisioning/role", { data: ids });

export const saveRole = async (role: EditableRole): Promise<Role["id"]> => {
  var data = await API.put(
    `/symphony-api/provisioning/role/${role.id ?? ""}`,
    role
  );
  return data.data;
};

export const getUserRoles = async (
  userRole?: Array<string>
): Promise<Role[]> => {
  const params: any = {
    userRole: userRole,
  };

  const response = await API.get<Role[]>(
    `/symphony-api/provisioning/user/role`,
    {
      params,
    }
  );

  return response.data;
};

export const createUser = async (
  user: UserToAdd,
  sendWelcomeEmail?: boolean
): Promise<string> => {
  const response = await API.put<string>(
    `/symphony-api/provisioning/user`,
    user,
    {
      params: {
        sendWelcomeEmail: sendWelcomeEmail,
      },
    }
  );

  return response.data;
};

export const createUserSelfActivation = async (
  data: UserSelfActivation
): Promise<string> => {
  const response = await API.post<string>(
    `/symphony-api/provisioning/user/activation`,
    data
  );

  return response.data;
};

export const getUsers = async (
  user: UserFilters,
  maxResults?: number,
  orderBy?: string,
  startIndex?: number
): Promise<ListPage<User>> =>
  (
    await API.post<ListPage<User>>(`/symphony-api/provisioning/user`, user, {
      params: {
        maxResults,
        orderBy,
        startIndex,
      },
    })
  ).data;

export const updateUser = async (
  user: User,
  userId?: string,
  sendWelcomeEmail?: boolean
): Promise<void> => {
  await API.put(
    `/symphony-api/provisioning/user/${userId}?sendWelcomeEmail=${sendWelcomeEmail}`,
    user
  );
};

export const deleteUsers = (userIds: User["id"][]) =>
  API.delete(`/symphony-api/provisioning/user`, { data: userIds });

export const changePassword = async (
  userId: string,
  oldPassword: string,
  newPassword: string
) =>
  await API.put(`/symphony-api/provisioning/user/${userId}/pwd`, {
    oldPassword,
    newPassword,
  });

export const getProducers = async (accountId: string): Promise<User[]> => {
  const params: any = {
    accountId: accountId,
  };

  const response = await API.get<User[]>(`/symphony-api/ticket/user`, {
    params,
  });

  return response.data;
};

export const getDisplayName = (user?: User) => {
  if (!user) return "(-)";
  const { firstName, lastName } = user;

  if (firstName && lastName) return `(${firstName} ${lastName})`;
  if (firstName) return `(${firstName})`;
  if (lastName) return `(${lastName})`;
  return "";
};

export const getCurrentUser = async () =>
  (await API.get<AuthenticatedUser>("/symphony-api/provisioning/user/me")).data;

export const getUser = async (userId: string) => {
  const response = await API.get(`/symphony-api/provisioning/user/${userId}`);
  return response.data;
};

export const resetPassword = async (userId: string, newPassword: string) => {
  const response = await API.post(
    `/symphony-api/provisioning/user/${userId}/pwd/reset`,
    {
      newPassword,
    }
  );
  return response;
};

export const resendWelcomeEmail = async (userId: string) => {
  const response = await API.post(
    `/symphony-api/provisioning/user/${userId}/resend/welcome`
  );
  return response;
};

export const postUsers = async (
  accountId: string,
  data: FormData,
  setProgress: Dispatch<SetStateAction<number>>
) => {
  return (
    await API.post(
      `/symphony-api/provisioning/user/import?accountId=${accountId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.progress ?? 0) * 100;
          setProgress(progress);
        },
      }
    )
  ).data;
};
